import {
  TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import confetti from "canvas-confetti"
import { CheckCircle2Icon, PartyPopperIcon, RocketIcon, X } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import { TourId } from "../types/tour.types"

interface TourCelebrationModalProps {
  isOpen: boolean
  completedTourId: TourId | null
  onContinue: () => void
  onExit: () => void
}

const TOUR_CONFIG: Record<
  TourId,
  {
    icon: React.ReactNode
    completedTitleKey: TranslationKeys
    completedDescriptionKey: TranslationKeys
    nextTourNameKey?: TranslationKeys
  }
> = {
  questionnaire: {
    icon: <CheckCircle2Icon className="text-green-400 size-8" />,
    completedTitleKey: "TOUR/CELEBRATION/QUESTIONNAIRE/TITLE",
    completedDescriptionKey: "TOUR/CELEBRATION/QUESTIONNAIRE/DESCRIPTION",
    nextTourNameKey: "TOUR/CELEBRATION/NEXT_TOUR/AI_CHAT",
  },
  "ai-chat": {
    icon: <RocketIcon className="text-blue-400 size-8" />,
    completedTitleKey: "TOUR/CELEBRATION/AI_CHAT/TITLE",
    completedDescriptionKey: "TOUR/CELEBRATION/AI_CHAT/DESCRIPTION",
    nextTourNameKey: "TOUR/CELEBRATION/NEXT_TOUR/DATABASE",
  },
  database: {
    icon: <PartyPopperIcon className="size-8 text-yellow1" />,
    completedTitleKey: "TOUR/CELEBRATION/FINAL/TITLE",
    completedDescriptionKey: "TOUR/CELEBRATION/FINAL/DESCRIPTION",
  },
}

const getTourIndex = (tourId: TourId): number => {
  if (tourId === "questionnaire") return 1
  if (tourId === "ai-chat") return 2
  return 3
}

export const TourCelebrationModal: React.FC<TourCelebrationModalProps> = ({
  isOpen,
  completedTourId,
  onContinue,
  onExit,
}) => {
  const { translate } = useTranslation()
  const hasAnimated = useRef(false)
  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const [finalTourScreen, setFinalTourScreen] = useState<1 | 2>(1)

  useEffect(() => {
    if (!isOpen) {
      hasAnimated.current = false
      setFinalTourScreen(1)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    const canvas = confettiCanvasRef.current
    if (!canvas || hasAnimated.current) {
      return
    }

    hasAnimated.current = true
    const customConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: false,
    })

    const duration = 2000
    const end = Date.now() + duration

    const fireConfetti = () => {
      customConfetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ["#FFD700", "#FFA500", "#FF6347", "#87CEEB", "#98FB98"],
        shapes: ["star"],
        scalar: 1.2,
      })

      customConfetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ["#FFD700", "#FFA500", "#FF6347", "#87CEEB", "#98FB98"],
        shapes: ["star"],
        scalar: 1.2,
      })

      customConfetti({
        particleCount: 5,
        angle: 90,
        spread: 100,
        origin: { x: 0.5, y: 0 },
        colors: [
          "#FFD700",
          "#FFA500",
          "#FF6347",
          "#87CEEB",
          "#98FB98",
          "#DDA0DD",
        ],
      })

      if (Date.now() < end) {
        animationFrameRef.current = requestAnimationFrame(fireConfetti)
      }
    }

    fireConfetti()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      customConfetti.reset()
    }
  }, [isOpen])

  if (!isOpen || !completedTourId) return null

  const config = TOUR_CONFIG[completedTourId]
  const isFinalTour = completedTourId === "database"
  const tourIndex = getTourIndex(completedTourId)

  return (
    <>
      <div className="fixed inset-0 z-99998 bg-black/80" />

      <canvas
        ref={confettiCanvasRef}
        className="pointer-events-none fixed inset-0 z-99999 h-full w-full"
      />

      <div className="fixed left-1/2 top-1/2 z-100000 w-full max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-lg border-2 border-yellow1/50 bg-card p-6 shadow-2xl">
        {isFinalTour && finalTourScreen === 1 ? (
          <div className="-mr-2 mb-4 flex items-center">
            <div className="w-6" />
            <p className="text-zinc-400 flex-1 text-center text-sm leading-relaxed">
              {translate("TOUR/CELEBRATION/FINAL/ALMOST_DONE")}
            </p>
            <button
              onClick={onExit}
              className="cursor-pointer border-none bg-transparent p-1 opacity-70 transition-opacity hover:opacity-100"
              aria-label={translate("TOUR/CELEBRATION/CLOSE")}
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>
        ) : (
          <button
            onClick={onExit}
            className="absolute right-4 top-4 cursor-pointer border-none bg-transparent p-1 opacity-70 transition-opacity hover:opacity-100"
            aria-label={translate("TOUR/CELEBRATION/CLOSE")}
          >
            <X className="h-4 w-4 text-white" />
          </button>
        )}

        {(!isFinalTour || finalTourScreen === 2) && (
          <div className="mb-4 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow1/10">
              {config.icon}
            </div>
            <h2 className="mb-3 text-xl font-semibold text-white">
              {translate(config.completedTitleKey)}
            </h2>
            <p className="text-zinc-400 text-sm leading-relaxed">
              {translate(config.completedDescriptionKey)}
            </p>
          </div>
        )}

        {(!isFinalTour || finalTourScreen === 2) && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="flex gap-1.5">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`h-2 w-8 rounded-full transition-colors ${
                    step <= tourIndex ? "bg-yellow1" : "bg-grey4"
                  }`}
                />
              ))}
            </div>
            <span className="text-zinc-400 text-sm font-medium">
              {tourIndex}/3
            </span>
          </div>
        )}

        {!isFinalTour && config.nextTourNameKey && (
          <div className="border-zinc-700 bg-zinc-800/50 mt-4 rounded-lg border p-3">
            <p className="text-zinc-400 text-center text-sm">
              {translate("TOUR/CELEBRATION/NEXT_UP")}{" "}
              <span className="font-medium text-white">
                {translate(config.nextTourNameKey)}
              </span>
            </p>
          </div>
        )}

        {isFinalTour && finalTourScreen === 1 && (
          <div className="mt-4 rounded-lg border border-yellow1/30 bg-yellow1/5 p-4 text-center">
            <h3 className="mb-3 whitespace-pre-line text-xl font-semibold text-white">
              {translate("TOUR/CELEBRATION/FINAL/EXTENSION_HEADING")}
            </h3>
            <p className="text-zinc-400 mb-4 text-sm leading-relaxed">
              {translate("TOUR/CELEBRATION/FINAL/EXTENSION_INFO")}
            </p>
            <a
              href="https://turnus.ai/extension-interaction"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setFinalTourScreen(2)}
              className="block w-full rounded-lg bg-yellow1 px-4 py-3 text-center text-sm font-semibold text-black no-underline transition-colors hover:bg-yellow1/90"
            >
              {translate("TOUR/CELEBRATION/FINAL/EXTENSION_BUTTON")}
            </a>
          </div>
        )}

        {(!isFinalTour || finalTourScreen === 2) && (
          <button
            onClick={onContinue}
            type="button"
            className="mt-6 w-full cursor-pointer rounded-lg border-none bg-yellow1 px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-yellow1/90"
          >
            {isFinalTour
              ? translate("TOUR/CELEBRATION/FINAL/CTA")
              : translate("TOUR/CELEBRATION/CONTINUE")}
          </button>
        )}
      </div>
    </>
  )
}
